import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';
import CustomAutoComplete from '../UI/CustomAutoComplete';
import ConfirmationDialog from '../UI/Dialogs/ConfirmationDialog';
import ScenarioFormRow from './ScenarioFormRow';
import { ScenarioScope, ScenarioScopeOption } from './useScenarioScope';

interface ScenarioScopeFieldProps {
    scope: ScenarioScope;
    isLoading?: boolean;
    disabled?: boolean;
}

// The board's focal control: the multiselect at the top of a scenario form that says WHICH entities the
// scenario is about (CALL-783). It replaces the per-grid "ADD WORKTYPE" button, its picker dialog and its
// trash column — one removal mechanism, so the selection and the rows can never drift apart.
//
// Same md=6 geometry as the metadata fields above it (the reason budget-adjustment's scope looked
// misaligned: it used a bare maxWidth). There is ONE slot, this one, for all five types — the per-type
// placement that once put budget adjustment's list beside the impact table is gone; see SCOPE_BY_TYPE.
const ScenarioScopeField = ({ scope, isLoading, disabled }: ScenarioScopeFieldProps) => {
    const { t } = useTranslation('budget');

    if (!scope.entity) return null;

    // One gesture, one dialog — but the clear (X) drops the whole selection at once, so the prompt has to
    // account for every entity it is about to discard rather than name one and take the rest with it.
    const removals = scope.pendingRemovals;
    const removalNames = removals.map((option) => option.label).join(', ');

    return (
        <>
            <ScenarioFormRow>
                {/* The scope rule (what a pick creates, what a removal discards) is a help icon BESIDE the
                    field, not a caption under it: as a caption it ran three lines and pushed the grid below
                    the fold. Beside rather than a Tooltip on the multiselect itself — that would fire on
                    every hover while picking options and overlap the open dropdown. */}
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <CustomAutoComplete<ScenarioScopeOption, true>
                            options={scope.options}
                            value={scope.selected}
                            onChange={(next) => scope.select(next.map((option) => option.id))}
                            label={t(scope.labelKey)}
                            labelKeys={['label']}
                            isMultiSelect
                            loading={isLoading}
                            disabled={disabled}
                            isOptionEqualToValue={(option, selected) => option.id === selected.id}
                            size="small"
                        />
                    </Box>
                    <Tooltip title={t(scope.hintKey)} slotProps={{ tooltip: { sx: { maxWidth: 460 } } }}>
                        {/* An IconButton, so the note is reachable by keyboard — a bare icon is not a tab
                            stop, and the tooltip would then be mouse-only. */}
                        <IconButton size="small" aria-label={t('action.showScopeNote')}>
                            <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </ScenarioFormRow>

            {/* Deselecting an entity that already holds values throws them away on the next Save — and the
                board offers no undo, so ask first. Cancel keeps the entity; Save is what makes it final. */}
            <ConfirmationDialog
                open={removals.length > 0}
                title={
                    removals.length > 1
                        ? t('dialog.scopeRemoveTitleMultiple', { count: removals.length })
                        : t('dialog.scopeRemoveTitle', { name: removalNames })
                }
                text={
                    removals.length > 1
                        ? t('dialog.scopeRemoveBodyMultiple', { names: removalNames })
                        : t('dialog.scopeRemoveBody')
                }
                onClose={scope.cancelRemoval}
                onConfirm={scope.confirmRemoval}
            />
        </>
    );
};

export default ScenarioScopeField;
